import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

const formatWon = (n) => `₩${Number(n || 0).toLocaleString('ko-KR')}`;
const formatDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
};

const STATUS = {
  pending:   { label: '결제 대기', color: 'var(--warning)', bg: 'var(--warning-light)' },
  paid:      { label: '결제 완료', color: 'var(--success)', bg: 'var(--success-light)' },
  failed:    { label: '결제 실패', color: 'var(--danger)',  bg: 'var(--danger-light)' },
  cancelled: { label: '취소됨',    color: 'var(--text-tertiary)', bg: '#F2F4F6' },
};

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [store, setStore] = useState(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/billing/invoices/${id}`);
      setInvoice(data.invoice);
      setStore(data.store);
    } catch (e) {
      setError(e.response?.data?.error || '청구서를 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoice(); /* eslint-disable-next-line */ }, [id]);

  const handlePay = async () => {
    setPaying(true);
    try {
      await api.post(`/admin/billing/invoices/${id}/pay`, { method: 'card' });
      await fetchInvoice();
    } catch (e) {
      setError(e.response?.data?.error || '결제 처리에 실패했습니다');
    } finally {
      setPaying(false);
    }
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <div className="spinner spinner-dark" style={{ width: 32, height: 32, borderWidth: 3 }} />
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="page-container">
        <div style={{ padding: 40, textAlign: 'center' }}>
          <p style={{ color: 'var(--danger)' }}>{error || '청구서를 찾을 수 없습니다'}</p>
          <button className="btn btn-outline" onClick={() => navigate('/billing')} style={{ marginTop: 16 }}>
            청구서 목록으로
          </button>
        </div>
      </div>
    );
  }

  const statusMeta = STATUS[invoice.status] || STATUS.pending;
  const lineItems = Array.isArray(invoice.line_items) ? invoice.line_items : [];

  return (
    <div className="page-container" style={{ maxWidth: 900 }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .page-container { background: #fff !important; padding: 0 !important; }
          .invoice-doc { box-shadow: none !important; border: none !important; }
          @page { margin: 20mm; }
        }
      `}</style>

      {/* Header actions */}
      <div className="no-print" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
      }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/billing')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          <span style={{ marginLeft: 6 }}>목록으로</span>
        </button>
        <div style={{ display: 'flex', gap: 10 }}>
          {invoice.status === 'pending' && (
            <button className="btn btn-primary" onClick={handlePay} disabled={paying}>
              {paying ? '결제 중...' : `${formatWon(invoice.total)} 결제하기`}
            </button>
          )}
          <button className="btn btn-outline" onClick={handlePrint}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            <span style={{ marginLeft: 6 }}>인쇄</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="no-print" style={{
          padding: 12,
          background: 'var(--danger-light)',
          color: 'var(--danger)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: 16,
          fontSize: '0.85rem',
        }}>
          {error}
        </div>
      )}

      {/* Invoice document */}
      <div className="invoice-doc" style={{
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '48px 56px',
        boxShadow: 'var(--shadow-card)',
      }}>
        {/* Document header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 40 }}>
          <div>
            <div style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: 1.5,
              color: 'var(--primary)',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}>
              INVOICE
            </div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px' }}>
              청구서
            </h1>
            <div style={{
              marginTop: 8,
              fontSize: '0.85rem',
              fontFamily: 'monospace',
              color: 'var(--text-secondary)',
            }}>
              {invoice.invoice_number}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              display: 'inline-block',
              padding: '6px 14px',
              background: statusMeta.bg,
              color: statusMeta.color,
              borderRadius: 999,
              fontSize: '0.8rem',
              fontWeight: 700,
              marginBottom: 10,
            }}>
              {statusMeta.label}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
              발행일: {formatDate(invoice.created_at)}<br />
              납부 기한: {formatDate(invoice.due_date)}
              {invoice.paid_at && (
                <>
                  <br />결제일: {formatDate(invoice.paid_at)}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bill from / to */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 32,
          marginBottom: 40,
          paddingBottom: 32,
          borderBottom: '1px solid var(--border)',
        }}>
          <div>
            <div style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 8,
            }}>
              발행자
            </div>
            <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: '1rem', marginBottom: 2 }}>
              QR Order Platform
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              대한민국<br />
              support@qrorder.kr
            </div>
          </div>
          <div>
            <div style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 8,
            }}>
              받는 분
            </div>
            <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: '1rem', marginBottom: 2 }}>
              {store?.name || '-'}
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {store?.owner_email || '-'}
            </div>
          </div>
        </div>

        {/* Line items */}
        <div style={{ marginBottom: 32 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--text)' }}>
                <th style={{
                  textAlign: 'left',
                  padding: '12px 0',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}>
                  내역
                </th>
                <th style={{
                  textAlign: 'right',
                  padding: '12px 12px 12px 0',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  width: 80,
                }}>
                  수량
                </th>
                <th style={{
                  textAlign: 'right',
                  padding: '12px 12px 12px 0',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  width: 140,
                }}>
                  단가
                </th>
                <th style={{
                  textAlign: 'right',
                  padding: '12px 0',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  width: 140,
                }}>
                  합계
                </th>
              </tr>
            </thead>
            <tbody>
              {lineItems.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: '16px 0', color: 'var(--text-tertiary)', fontSize: '0.88rem' }}>
                    구독 요금
                  </td>
                </tr>
              ) : (
                lineItems.map((li, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '16px 0', fontSize: '0.9rem', color: 'var(--text)' }}>
                      {li.description}
                    </td>
                    <td style={{ padding: '16px 12px 16px 0', fontSize: '0.9rem', textAlign: 'right', color: 'var(--text-secondary)' }}>
                      {li.quantity || 1}
                    </td>
                    <td style={{ padding: '16px 12px 16px 0', fontSize: '0.9rem', textAlign: 'right', color: 'var(--text-secondary)' }}>
                      {formatWon(li.unit_price || 0)}
                    </td>
                    <td style={{ padding: '16px 0', fontSize: '0.9rem', textAlign: 'right', fontWeight: 600, color: 'var(--text)' }}>
                      {formatWon(li.total || 0)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 32,
        }}>
          <div style={{ width: 320 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              fontSize: '0.88rem',
            }}>
              <span style={{ color: 'var(--text-secondary)' }}>소계</span>
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>{formatWon(invoice.amount)}</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              fontSize: '0.88rem',
            }}>
              <span style={{ color: 'var(--text-secondary)' }}>부가세 (10%)</span>
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>{formatWon(invoice.vat)}</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '14px 0',
              borderTop: '2px solid var(--text)',
              marginTop: 6,
            }}>
              <span style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text)' }}>총 합계</span>
              <span style={{ fontWeight: 800, fontSize: '1.2rem', color: 'var(--primary)' }}>
                {formatWon(invoice.total)}
              </span>
            </div>
          </div>
        </div>

        {/* Payment status */}
        <div style={{
          padding: 20,
          background: invoice.status === 'paid' ? 'var(--success-light)' : 'var(--bg)',
          borderRadius: 'var(--radius-sm)',
          border: `1px solid ${invoice.status === 'paid' ? 'var(--success)' : 'var(--border)'}`,
        }}>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            color: invoice.status === 'paid' ? 'var(--success)' : 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            marginBottom: 6,
          }}>
            결제 상태
          </div>
          <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
            {invoice.status === 'paid' ? (
              <>
                <strong>결제 완료</strong> · {formatDate(invoice.paid_at)}
                {invoice.payment_method && (
                  <>
                    <br />
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                      결제 수단: {invoice.payment_method}
                      {invoice.payment_reference && ` · ${invoice.payment_reference}`}
                    </span>
                  </>
                )}
              </>
            ) : invoice.status === 'pending' ? (
              <>
                <strong>결제 대기 중</strong>
                <br />
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                  납부 기한: {formatDate(invoice.due_date)}
                </span>
              </>
            ) : (
              <strong>{statusMeta.label}</strong>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 40,
          paddingTop: 20,
          borderTop: '1px solid var(--border)',
          textAlign: 'center',
          fontSize: '0.75rem',
          color: 'var(--text-tertiary)',
        }}>
          본 청구서는 전자적으로 생성된 문서입니다. 문의: support@qrorder.kr
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailPage;
